import { isEmpty, isEmail, formatMobilePhone } from '@jjj/components';
import { isMobilePhone } from '../../../../../../helpers/validators';
import { keyBy } from '../../../../../../helpers/core';
import { createReducer, getError } from '../../../../../../helpers/reducer';
import { t } from '../../../../../../i18n';
import actions from './actions';

const initState = {
  manager: null,
  editForm: null,
  isEdit: false,
  loading: false,
  formInfo: {},
};

const initialManagerData = {
  avatar_url: '',
  departments: [
    { id: 1, name: 'FOH' },
    { id: 2, name: 'BOH' },
  ],
  email: '',
  first_name: '',
  last_name: '',
  notify_job_is_fulfilled: false,
  notify_message_board_post: false,
  notify_worker_begins_shift: false,
  notify_worker_completes_shift: false,
  phone_number: '',
  role: '',
};

function validate(attr, value, params) {
  let error = '';

  if (attr === 'role') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.managers.role_label') });
    }
  }

  if (attr === 'first_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.subUnits.subUnit.managers.first_name_label'),
      });
    }
  }

  if (attr === 'last_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.subUnits.subUnit.managers.last_name_label'),
      });
    }
  }

  if (attr === 'email') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.subUnits.subUnit.managers.email_label'),
      });
    } else if (!isEmail(value)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.subUnits.subUnit.managers.email_label'),
      });
    }
  }

  if (attr === 'phone_number') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.subUnits.subUnit.managers.phone_number_label'),
      });
    } else if (!isMobilePhone(formatMobilePhone(value), params?.phoneNumberCountry)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.subUnits.subUnit.managers.phone_number_label'),
      });
    }
  }

  return error;
}

function formatSaveError(attr, data) {
  const error = getError(attr, data);

  if (attr === 'email' && error.toLowerCase().includes('already been taken')) {
    return t('dash.subUnits.subUnit.managers.email_already_exists_error');
  }

  return error;
}

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const manager = data;

  return {
    ...state,
    manager,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function onEdit(state, { payload }) {
  const propsArray = Object.keys(payload.manager);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value:
        payload.manager[prop] && {}.hasOwnProperty.call(payload.manager[prop], 'id')
          ? payload.manager[prop].id
          : payload.manager[prop],
      error: validate(prop, payload.manager[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state, { payload }) {
  const { data } = payload;
  const manager = data;

  const role = state.formInfo.roles.find(roleItem => roleItem.id === manager.role);

  return {
    ...state,
    manager: {
      ...manager,
      role,
    },
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        error: formatSaveError(entry[0], data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onDelete(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialManagerData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialManagerData[prop],
      error: validate(prop, initialManagerData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    manager: initialManagerData,
    editForm,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value, params } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value, params),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormData(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: data,
  };
}

export { validate, formatSaveError };
export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.EDIT]: onEdit,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CANCEL]: onCancel,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.DELETE]: onDelete,
  [actions.CREATE]: onCreate,
  [actions.TOUCH]: onTouch,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
});
